import { useState, Fragment } from 'react';
import { Plus, Receipt, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MatrixGroup, MatrixCell, MatrixRow } from '../hooks/useTithes';
import { MONTH_NAMES, MONTH_NAMES_FULL } from '../types';
import type { Tithe } from '../types';
import type { BankAccount } from '@/features/settings/types';
import { formatMoney } from '@/features/receipts/utils/receiptFormat';
import { TithePaymentForm } from './TithePaymentForm';
import { TitheReportDialog } from './TitheReportDialog';

// Solid backgrounds on sticky cells so scrolled columns don't show through.
const STICKY_CARGO  = 'sticky left-0 z-10 hidden w-32 min-w-32 max-w-32 bg-card group-hover:bg-muted sm:table-cell';
const STICKY_NOMBRE = 'sticky left-0 z-10 min-w-40 bg-card group-hover:bg-muted sm:left-32';

function formatDate(iso: string) {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${String(y).slice(2)}`;
}

interface CellButtonProps {
  cell: MatrixCell;
  row: MatrixRow;
  year: number;
  month: number; // 1-indexed
  isFuture: boolean;
  isAdmin: boolean;
  bankAccounts: BankAccount[];
  onUpdated: () => void;
}

function CellButton({ cell, row, year, month, isFuture, isAdmin, bankAccounts, onUpdated }: CellButtonProps) {
  const [open, setOpen] = useState(false);

  if (cell.outOfTenure) {
    return <td className="border border-border/40 bg-muted/30 p-1 text-center text-xs text-muted-foreground/40">—</td>;
  }

  const hasTithe = !!cell.tithe;
  // Months that haven't ended yet aren't "pending", just not due.
  const pending = !hasTithe && !isFuture;

  const contextLine1 = row.label;
  const contextLine2 = row.roleLabel
    ? `${row.roleLabel} — ${MONTH_NAMES_FULL[month - 1]} ${year}`
    : `${MONTH_NAMES_FULL[month - 1]} ${year}`;

  const content = hasTithe ? (
    <>
      <p className={cn('font-semibold', cell.late && 'text-red-600 dark:text-red-400')}>
        {formatMoney(cell.tithe!.amount)}
      </p>
      <p className={cn('text-[10px]', cell.late ? 'text-red-500' : 'text-muted-foreground')}>
        {formatDate(cell.tithe!.paid_at)}
      </p>
    </>
  ) : isAdmin ? (
    <Plus className="mx-auto size-3.5 opacity-40 group-hover:opacity-100" />
  ) : null;

  return (
    <>
      <td
        title={cell.late ? 'Pagado después del fin de mes' : undefined}
        className={cn(
          'relative border border-border/40 px-1 py-1 text-center text-xs',
          hasTithe && cell.late && 'bg-red-50 dark:bg-red-950/30',
          pending && 'bg-amber-50/50 dark:bg-amber-950/20',
        )}
      >
        {cell.hasReceipt && (
          <Receipt aria-label="Recibo emitido" className="absolute right-0.5 top-0.5 size-3 text-muted-foreground" />
        )}
        {isAdmin ? (
          <button
            onClick={() => setOpen(true)}
            aria-label={`${hasTithe ? 'Editar' : 'Registrar'} diezmo ${MONTH_NAMES_FULL[month - 1]} — ${row.label}`}
            className={cn(
              'flex min-h-10 w-full flex-col items-center justify-center rounded px-1 py-0.5 transition-colors hover:bg-black/5 dark:hover:bg-white/5',
              !hasTithe && 'text-muted-foreground',
            )}
          >
            {content}
          </button>
        ) : content}
      </td>

      {open && (
        <TithePaymentForm
          subjectId={cell.subjectId}
          subjectType={cell.subjectType}
          contextLine1={contextLine1}
          contextLine2={contextLine2}
          year={year}
          month={month}
          existing={cell.tithe as Tithe | null}
          ministerId={row.ministerId}
          payerCi={row.ministerDni}
          bankAccounts={bankAccounts}
          onClose={() => setOpen(false)}
          onSaved={onUpdated}
        />
      )}
    </>
  );
}

const GROUP_HEADER_STYLES: Record<string, string> = {
  NATIONAL:     'bg-purple-900/15 text-purple-900 dark:bg-purple-100/10 dark:text-purple-300',
  DISTRICT:     'bg-primary/10 text-primary',
  DISTRICT_ORG: 'bg-primary/10 text-primary',
  REGION:       'bg-sky-900/10 text-sky-800 dark:bg-sky-100/10 dark:text-sky-300',
  INSTITUTION:  'bg-emerald-900/10 text-emerald-800 dark:bg-emerald-100/10 dark:text-emerald-300',
};

function groupLabel(group: MatrixGroup): string {
  if (group.orgType === 'NATIONAL') return 'Directorio Nacional';
  if (group.orgType === 'DISTRICT_ORG') return 'Distritos';
  if (group.orgType === 'INSTITUTION') return group.orgName;
  if (group.orgCode) return `${group.orgCode} — ${group.orgName}`;
  return group.orgName;
}

/** Per-month and overall sums for a set of rows. */
function sumRows(rows: MatrixRow[]): { months: number[]; total: number } {
  const months = Array<number>(12).fill(0);
  for (const row of rows) {
    row.cells.forEach((c, i) => { months[i] += c.tithe?.amount ?? 0; });
  }
  return { months, total: months.reduce((a, b) => a + b, 0) };
}

function TotalsRow({ label, sums, grand }: { label: string; sums: { months: number[]; total: number }; grand?: boolean }) {
  return (
    <tr className={cn('group font-semibold', grand ? 'bg-primary/10' : 'bg-muted/40')}>
      <td className={cn(STICKY_CARGO, grand ? 'bg-muted' : 'bg-muted')} />
      <td className={cn(STICKY_NOMBRE, 'border border-border/40 bg-muted px-3 py-1.5 text-xs uppercase tracking-wide')}>
        {label}
      </td>
      {sums.months.map((v, i) => (
        <td key={i} className="border border-border/40 px-1 py-1.5 text-center text-xs">
          {v > 0 ? formatMoney(v) : '—'}
        </td>
      ))}
      <td className="border border-border/40 px-3 py-1.5 text-right text-primary">{formatMoney(sums.total)}</td>
    </tr>
  );
}

export function TithesLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5"><span className="size-3 rounded-sm border border-border bg-card" />Pagado</span>
      <span className="flex items-center gap-1.5" title="Pagado después del fin de mes">
        <span className="size-3 rounded-sm bg-red-100 dark:bg-red-950/60" />Pagado con retraso
      </span>
      <span className="flex items-center gap-1.5"><span className="size-3 rounded-sm bg-amber-100 dark:bg-amber-950/60" />Pendiente</span>
      <span className="flex items-center gap-1.5"><span className="size-3 rounded-sm bg-muted text-center leading-3">—</span>Fuera de gestión</span>
      <span className="flex items-center gap-1.5"><Receipt className="size-3" />Recibo emitido</span>
    </div>
  );
}

interface TithesAnnualMatrixProps {
  groups: MatrixGroup[];
  year: number;
  isAdmin: boolean;
  bankAccounts: BankAccount[];
  onUpdated: () => void;
}

export function TithesAnnualMatrix({ groups, year, isAdmin, bankAccounts, onUpdated }: TithesAnnualMatrixProps) {
  const [reportRow, setReportRow] = useState<MatrixRow | null>(null);

  if (groups.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-10 text-center text-sm text-muted-foreground">
        No hay registros para los filtros seleccionados.
      </div>
    );
  }

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const isFutureMonth = (m: number) => year > currentYear || (year === currentYear && m > currentMonth);

  const thBase = 'sticky top-0 border border-border/40 bg-muted';

  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-border">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className={cn(thBase, 'z-30 hidden w-32 min-w-32 max-w-32 px-3 py-2 text-left font-semibold sm:sticky sm:left-0 sm:table-cell')}>Cargo</th>
            <th className={cn(thBase, 'left-0 z-30 min-w-40 px-3 py-2 text-left font-semibold sm:left-32')}>Nombre</th>
            {MONTH_NAMES.map((m) => (
              <th key={m} className={cn(thBase, 'z-20 p-2 text-center font-semibold uppercase tracking-wide')}>
                {m}
              </th>
            ))}
            <th className={cn(thBase, 'z-20 px-3 py-2 text-right font-semibold text-primary')}>TOTAL Bs.</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <Fragment key={group.orgId}>
              {/* Group header row — label sticks to the left while the months scroll */}
              <tr className={cn('border border-border/40', GROUP_HEADER_STYLES[group.orgType] ?? 'bg-primary/10 text-primary')}>
                <td colSpan={15} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider">
                  <div className="sticky left-3 w-max">{groupLabel(group)}</div>
                </td>
              </tr>

              {group.rows.map((row) => (
                <tr key={row.subjectId} className="group">
                  {/* Cargo column: role label for position rows, empty for org rows */}
                  <td className={cn(STICKY_CARGO, 'border border-border/40 px-3 py-1 text-xs font-medium text-muted-foreground')}>
                    {row.roleLabel}
                  </td>

                  <td className={cn(STICKY_NOMBRE, 'border border-border/40 px-3 py-1')}>
                    <div className="flex items-start justify-between gap-1">
                      <p className="font-medium">{row.label}</p>
                      <button
                        onClick={() => setReportRow(row)}
                        aria-label={`Reporte de diezmos de ${row.label}`}
                        title="Reporte por rango de años"
                        className="shrink-0 rounded p-0.5 text-muted-foreground opacity-60 hover:bg-muted hover:text-foreground group-hover:opacity-100"
                      >
                        <FileText className="size-3.5" />
                      </button>
                    </div>
                    {/* Below sm the Cargo column is hidden, so the role moves under the name */}
                    {row.roleLabel && (
                      <p className="text-xs text-muted-foreground sm:hidden">{row.roleLabel}</p>
                    )}
                    {row.sublabel && (
                      <p className="text-xs text-muted-foreground">{row.sublabel}</p>
                    )}
                  </td>

                  {row.cells.map((cell, idx) => (
                    <CellButton
                      key={idx}
                      cell={cell}
                      row={row}
                      year={year}
                      month={idx + 1}
                      isFuture={isFutureMonth(idx + 1)}
                      isAdmin={isAdmin}
                      bankAccounts={bankAccounts}
                      onUpdated={onUpdated}
                    />
                  ))}

                  <td className="border border-border/40 px-3 py-1 text-right font-bold text-primary">
                    {row.totalPaid > 0 ? formatMoney(row.totalPaid) : '—'}
                  </td>
                </tr>
              ))}

              <TotalsRow label={`Total ${groupLabel(group)}`} sums={sumRows(group.rows)} />
            </Fragment>
          ))}
          {groups.length > 1 && (
            <TotalsRow label="Total general" sums={sumRows(groups.flatMap((g) => g.rows))} grand />
          )}
        </tbody>
      </table>
      {reportRow && (
        <TitheReportDialog
          subject={{
            subjectType: reportRow.subjectType,
            subjectId: reportRow.subjectId,
            ministerId: reportRow.ministerId,
            label: reportRow.label,
            sublabel: reportRow.sublabel,
            roleLabel: reportRow.roleLabel,
          }}
          defaultYear={year}
          onClose={() => setReportRow(null)}
        />
      )}
    </div>
  );
}
